import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/**
 * 현장 › 아차사고일지 · 근로자 의견 청취(백엔드 SafeVoiceController · V576 · DOC-136).
 * 근로자는 현장 QR(`/voice/:token`)로 로그인 없이 낸다 — 공개 쪽은 `pages/public/VoiceReportPage.tsx`.
 */

export type VoiceKind = 'NEAR_MISS' | 'OPINION';
export type VoiceStatus = 'RECEIVED' | 'IN_PROGRESS' | 'DONE';

export const NEAR_MISS_CATEGORIES = ['추락', '끼임', '넘어짐', '부딪힘', '맞음(낙하·비래)', '감전', '화재·폭발', '붕괴·무너짐', '베임·찔림', '기타'];
export const OPINION_CATEGORIES = ['시설', '작업방법', '보호구', '휴게', '기타'];

export const STATUS_LABEL: Record<VoiceStatus, string> = { RECEIVED: '접수', IN_PROGRESS: '조치 중', DONE: '조치 완료' };
export const STATUS_BADGE: Record<VoiceStatus, string> = { RECEIVED: 'badge-red', IN_PROGRESS: 'badge-yellow', DONE: 'badge-green' };

export type VoiceLink = { siteId: string; token: string; updatedAt: string | null };

export type VoiceReport = {
  id: string;
  kind: VoiceKind;
  category: string;
  location: string | null;
  content: string;
  occurredOn: string | null;
  reporterName: string | null;
  reporterPhone: string | null;
  affiliation: string | null;
  status: VoiceStatus;
  actionNote: string | null;
  reply: string | null;
  actionByName: string | null;
  actionAt: string | null;
  createdAt: string;
  /** 사진 내려받기 주소(presigned, V581). 서버 재시작 전에는 칸이 없다. */
  photos?: string[] | null;
};

/** 근로자가 붙이는 사진 — 장수 · 줄이는 크기. 서버 상한은 장당 2.5MB(SafeVoiceService). */
export const MAX_VOICE_PHOTOS = 3;
export const VOICE_PHOTO_PROFILE = { maxEdge: 1600, quality: 0.8 };

export type VoiceHandle = { status: VoiceStatus; actionNote: string; reply: string };

export function voiceUrl(token: string): string {
  return `${window.location.origin}/voice/${token}`;
}

const base = (siteId: string) => `/v1/safe/sites/${siteId}/voice`;

export function useVoiceLink(siteId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['safe-voice-link', siteId],
    queryFn: () => apiFetch<VoiceLink>(`${base(siteId)}/link`),
    enabled,
  });
}

export function useVoiceReissue(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<VoiceLink>(`${base(siteId)}/link/reissue`, { method: 'POST' }),
    onSuccess: (link) => qc.setQueryData(['safe-voice-link', siteId], link),
  });
}

export function useVoiceReports(siteId: string, kind: VoiceKind) {
  return useQuery({
    queryKey: ['safe-voice-reports', siteId, kind],
    queryFn: () => apiFetch<VoiceReport[]>(`${base(siteId)}/reports?kind=${kind}`),
  });
}

export function useVoiceHandle(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: VoiceHandle }) =>
      apiFetch<VoiceReport>(`${base(siteId)}/reports/${id}`, { method: 'PUT', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['safe-voice-reports', siteId] }),
  });
}
